import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import './calendar.css';
import { Service } from '../../service';
import { SimpleBadge } from '../simpleBadge';
import { Button } from '../button';
import { Title } from '../title';
import MyWorkWeek from '../customWeekView';
import { Activity, Location } from '../../types/types';

import 'moment/locale/pt-br';

const localizer = momentLocalizer(moment);

interface EventComponentProps {
    event: Activity;
    selected?: boolean;
    method?: (event: Activity, evt: React.MouseEvent) => void;
}

const EventComponent: React.FC<EventComponentProps> = ({ event, selected = false, method = () => {} }) => {
    if (event.title === 'Quadra alugada') {
        return (
            <div
                onClick={(evt) => evt.stopPropagation()}
                className="aula-status aula-completa"
            >
                {event.title} <span className="spaces">{event.capacity}</span>
            </div>
        );
    }
    return (
        <>
            <div
                onClick={(evt) => {
                    evt.stopPropagation();
                    method(event, evt);
                }}
                className={`aula-status aula-aberta ${selected ? 'selected' : ''}`}
            >
                {event.title}{' '}
                <span className="spaces">
                    {event.ocupation}/{event.capacity}
                </span>
            </div>
        </>
    );
};

interface CalendarioProps {
    isMobile?: boolean;
    modalidade?: string;
    unidade?: string;
    location?: Location;
    onReturn?: (activities: Activity[]) => void;
}

export const Calendario: React.FC<CalendarioProps> = ({
    isMobile = false,
    modalidade,
    location,
    unidade,
    onReturn = (activities) => {
        // console.log(activities);
    },
}) => {
    const today = new Date();
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    const [events, setEvents] = useState<Activity[]>([]);
    const [filteredEvents, setFilteredEvents] = useState<Activity[]>([]);
    const [selectedActivities, setSelectedActivities] = useState<Activity[]>([]);
    const [selectedDay, setSelectedDay] = useState(today.getDay());
    const [selectedDate, setSelectedDate] = useState(today.toISOString().split('T')[0]);

    const maxSlots = Service.getRentalMaxSlots(location);

    const isSelected = (event: Activity) => selectedActivities.some((a) => a.idConfiguration === event.idConfiguration);

    const toggleSelect = (event: Activity) => {
        setSelectedActivities((prev) => {
            const exists = prev.some((a) => a.idConfiguration === event.idConfiguration);
            if (exists) {
                return prev.filter((a) => a.idConfiguration !== event.idConfiguration);
            }
            const nextCount = prev.length + 1;
            if (nextCount > maxSlots || !Service.getRentalContract(location, nextCount)) {
                return prev;
            }
            return [...prev, event];
        });
    };

    useEffect(() => {
        if (!unidade) {
            return;
        }
        (async () => {
            let activities = await Service.getActivities([location.rental.idActivity]);
            setEvents(activities);
        })();
    }, [modalidade, unidade, location]);

    useEffect(() => {
        if (events.length > 0) {
            let fevents = [...events];
            const horariosPassados: string[] = [];
            fevents = fevents.map((fe) => {
                return { ...fe, ...Service.setCalendarTime(fe, horariosPassados) };
            });
            setFilteredEvents(fevents);
        }
    }, [events]);

    const SelectDay = () => {
        let cWeek = [new Date(today.getTime())];
        for (var i = 1; i < days.length; i++) {
            let nd = new Date(cWeek[cWeek.length - 1].getTime());
            nd.setDate(nd.getDate() + 1);
            cWeek.push(nd);
        }

        return (
            <div className="day-row">
                <div>
                    <div>
                        {cWeek.map((day, key) => {
                            return (
                                <button
                                    key={key}
                                    onClick={(e) => {
                                        setSelectedDay(day.getDay());
                                        setSelectedDate(day.toISOString().split('T')[0]);
                                    }}
                                    className={`day-button ${day.getDay() === selectedDay ? 'selected' : ''}`}
                                >
                                    <span>{days[day.getDay()]}</span>
                                    <b>{day.getDate() < 10 ? '0' + day.getDate() : day.getDate()}</b>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const EventGrid = () => {
        let dayEvents = filteredEvents.filter((event) => {
            return event.activityDate.includes(selectedDate);
        });

        return (
            <div className="event-grid w-100">
                <div>
                    {(dayEvents.length > 0 &&
                        dayEvents.map((event, key) => {
                            return (
                                <div
                                    key={key}
                                    className={`${event.title === 'Quadra alugada' ? 'fechado' : ''} ${isSelected(event) ? 'selected' : ''} d-flex w-100`}
                                    onClick={(e) => {
                                        if (event.title !== 'Quadra alugada') {
                                            toggleSelect(event);
                                        }
                                    }}
                                >
                                    <div className="p-1">
                                        <b>{event.startString!.split(' ')[0]}</b>
                                    </div>
                                    <div className="p-1">
                                        <b className="d-block">{event.title}</b>
                                        <span className="d-block capitalize">{event.instructor}</span>
                                    </div>
                                </div>
                            );
                        })) || <p>Ops, parece que não há aulas para este dia.</p>}
                </div>
            </div>
        );
    };

    const CalendarComponents = {
        header: ({ date }: { date: Date }) => (
            <>
                <div
                    style={{ display: 'block' }}
                    onClick={(evt) => evt.stopPropagation()}
                >
                    <span>{days[moment(date).day()]}</span>
                    <b>{moment(date).date()}</b>
                </div>
            </>
        ),
        event: ({ event }: { event: Activity }) => {
            return (
                <EventComponent
                    event={event}
                    selected={isSelected(event)}
                    method={toggleSelect}
                />
            );
        },
    };

    const RentalCartBar = () => {
        return (
            <div className="rental-cart-bar">
                <div className="rental-cart-count">
                    {selectedActivities.length} de {maxSlots} horário{maxSlots > 1 ? 's' : ''} selecionado{selectedActivities.length !== 1 ? 's' : ''}
                </div>
                {selectedActivities.length > 0 && (
                    <div className="rental-cart-chips">
                        {selectedActivities.map((activity) => (
                            <SimpleBadge
                                key={activity.idConfiguration}
                                selected
                                text={`${activity.activityDate!.split('T')[0].split('-').reverse().join('/')} - ${activity.startTime}`}
                                onRemove={() => toggleSelect(activity)}
                            />
                        ))}
                    </div>
                )}
                <Button
                    text={
                        selectedActivities.length > 0
                            ? `Reservar ${selectedActivities.length} horário${selectedActivities.length !== 1 ? 's' : ''}`
                            : 'Selecione um horário'
                    }
                    enabled={selectedActivities.length > 0}
                    onClick={() => onReturn(selectedActivities)}
                />
            </div>
        );
    };

    return (
        <div className="calendar-agendamento">
            <Title text={'Selecione a data e o horário'} />
            {!isMobile && (
                <>
                    <h3 style={{ fontWeight: 400, fontSize: '1.12rem' }}>
                        As quadras em <span style={{ color: 'green' }}>verde</span> estão disponíveis para reserva.
                        {/*As aulas em <span style={{ color: 'red' }}>vermelho</span> não estão disponíveis para agendamento.*/}
                    </h3>
                </>
            )}
            {isMobile && (
                <>
                    <h5 style={{ fontWeight: 400, fontSize: '13px' }}>
                        As quadras em <span style={{ color: 'green' }}>verde</span> estão disponíveis para reserva.
                    </h5>
                </>
            )}
            {location?.rental &&
                (() => {
                    const rentalContract = Service.getRentalContract(location, selectedActivities.length || 1);
                    return (
                        rentalContract && (
                            <div className="rental-price-row">
                                <div className="rental-price-box rental-price-member">
                                    <span className="rental-price-label">Alunos</span>
                                    <span className="rental-price-value">{rentalContract.member.value}</span>
                                </div>
                                <div className="rental-price-box rental-price-not-member">
                                    <span className="rental-price-label">Não alunos</span>
                                    <span className="rental-price-value">{rentalContract.notMember.value}</span>
                                </div>
                            </div>
                        )
                    );
                })()}
            <br />
            {isMobile && (
                <div className="h-75">
                    <SelectDay />
                    <EventGrid />
                </div>
            )}

            {!isMobile && (
                <Calendar
                    localizer={localizer}
                    components={CalendarComponents as any}
                    events={filteredEvents}
                    startAccessor="start"
                    endAccessor="end"
                    defaultView={'myweek' as any}
                    views={
                        {
                            day: true,
                            month: true,
                            week: true,
                            myweek: MyWorkWeek,
                        } as any
                    }
                    style={{ height: 855 }}
                    min={moment('6:00am', 'h:mma').toDate()}
                    max={moment('10:59pm', 'h:mma').toDate()}
                    step={12}
                    timeslots={5}
                    dayLayoutAlgorithm={'no-overlap' as any}
                    messages={{
                        next: 'Próximo',
                        previous: 'Anterior',
                        today: 'Hoje',
                        month: 'Mês',
                        week: 'Semana',
                        day: 'Dia',
                    }}
                />
            )}
            <RentalCartBar />
        </div>
    );
};
